import React, { useState, useEffect } from "react";
import { useParams, useHistory } from "react-router-dom";
import { Button, Col, Empty, Row, Select, Table } from "antd";
import { CaretUpOutlined, CaretDownOutlined } from "@ant-design/icons";

import { useFetchConstant } from "apis/constant";
import {
  useGetAdviseeProposalReporting,
  useGetAdviseeProposals,
} from "apis/advisee";
import {
  constructShortenedFinancialNumber,
  getLabelOfConstant,
  guard,
} from "utils/general";
import { getReportingData } from "utils/reporting";
import { buildPNRProposalsUri } from "utils/routes";

import Title from "components/Title/Title";
import SectionContainer from "components/SectionContainer/SectionContainer";
import SummaryInfoCard from "components/SummaryInfoCard/SummaryInfoCard";

import {
  CoverageLabel,
  CoveragePriceText,
  CoverageYearText,
  CardContainer,
  ExtraInfoText,
  FinancialTitleText,
  FinancialValueText,
  Label,
  Selection,
} from "./RiskRatios.styles";

const { OptGroup, Option } = Select;

const DiffIcon = ({ beforeValue, afterValue }) => {
  const before = Number(String(beforeValue).replace(/[^0-9.-]/g, ""));
  const after = Number(String(afterValue).replace(/[^0-9.-]/g, ""));

  if (before > after) {
    return <CaretDownOutlined style={{ color: "#EC373A", marginLeft: 4 }} />;
  } else if (after > before) {
    return <CaretUpOutlined style={{ color: "#2AC940", marginLeft: 4 }} />;
  } else {
    return null;
  }
};

const getRiskRatioTableColumns = (title) => [
  {
    title,
    dataIndex: "label",
    key: "label",
    width: "25%",
    render: (value) => <Label>{value}</Label>,
  },
  {
    title: <FinancialTitleText>Before Optimisation</FinancialTitleText>,
    dataIndex: "value",
    key: "value",
    width: "15%",
    render: (value, rec) => (
      <FinancialValueText isInRange={rec.isInRange}>{value}</FinancialValueText>
    ),
  },
  {
    title: <FinancialTitleText>After Optimisation</FinancialTitleText>,
    dataIndex: "optimisedValue",
    key: "optimisedValue",
    width: "15%",
    render: (value, rec) => ({
      children: (
        <FinancialValueText isInRange={rec.isOptimisedInRange}>
          {value}
          <DiffIcon beforeValue={rec.value} afterValue={value} />
        </FinancialValueText>
      ),
      props: {
        style: { background: "#f1fcff" },
      },
    }),
  },
  {
    title: <FinancialTitleText>Optimal for Current Age</FinancialTitleText>,
    dataIndex: "range",
    key: "range",
    width: "20%",
    render: (value, rec) => <FinancialValueText>{value}</FinancialValueText>,
  },
  {
    title: "Note",
    dataIndex: "desc",
    key: "desc",
    width: "25%",
    render: (value) => <ExtraInfoText>{value}</ExtraInfoText>,
  },
];

const RiskRatioTable = ({ dataSource, columns }) => {
  return (
    <Row>
      <Col span={24}>
        <Table
          dataSource={dataSource}
          columns={columns}
          pagination={false}
          rowKey={(record) => record.label}
          style={{ width: "100%" }}
          scroll={{ x: 432 }}
        />
      </Col>
    </Row>
  );
};

const ProposalSelection = ({ riskRatiosData, value, onChange }) => {
  return (
    <span style={{ float: "right" }}>
      <b>Proposals: </b>
      <Selection value={value} onChange={onChange}>
        {riskRatiosData.map((v) => (
          <Option value={v._id} key={v._id}>
            {v.name}
          </Option>
        ))}
      </Selection>
    </span>
  );
};

const RiskRatios = ({ currentReporting, proposalList }) => {
  console.log(currentReporting, proposalList, 140);

  const [selectedProposal, setSelectedProposal] = useState("");
  const [naturalDeathCoverageData, setNaturalDeathCoverageData] = useState([]);
  const [
    accidentalDeathCoverageData,
    setAccidentalDeathCoverageData,
  ] = useState([]);
  const [medicalBenefitCoverageData, setMedicalBenefitCoverageData] = useState(
    []
  );
  const [tpdCoverageData, setTpdCoverageData] = useState([]);
  const [comatoseCoverageData, setComatoseCoverageData] = useState([]);

  useEffect(() => {
    if (!selectedProposal && proposalList && proposalList.length > 0) {
      setSelectedProposal(proposalList[0]._id);
    }
  }, [proposalList, currentReporting]);

  useEffect(() => {
    const targetProposal = proposalList.find((v) => v._id === selectedProposal);
    if (!targetProposal) return;

    const computedCompareRatio = getReportingData(
      currentReporting.riskRatioData,
      targetProposal.riskRatioData
    );
    setNaturalDeathCoverageData(computedCompareRatio.naturalDeathCoverageData);
    setAccidentalDeathCoverageData(
      computedCompareRatio.accidentalDeathCoverageData
    );
    setMedicalBenefitCoverageData(
      computedCompareRatio.medicalBenefitCoverageData
    );
    setTpdCoverageData(computedCompareRatio.tpdCoverageData);
    setComatoseCoverageData(computedCompareRatio.comatoseCoverageData);
  }, [selectedProposal, proposalList]);

  return (
    <>
      <SectionContainer>
        <Row justify="end" style={{ marginBottom: 16 }}>
          <ProposalSelection
            riskRatiosData={proposalList}
            value={selectedProposal}
            onChange={(value) => setSelectedProposal(value)}
          />
        </Row>

        <div
          style={{
            border: "1px solid #e8e8e8",
            borderRadius: 4,
          }}
        >
          <RiskRatioTable
            dataSource={naturalDeathCoverageData}
            columns={getRiskRatioTableColumns("Natural Death Coverage")}
          />
          <RiskRatioTable
            dataSource={accidentalDeathCoverageData}
            columns={getRiskRatioTableColumns("Accidental Death Coverage")}
          />
          <RiskRatioTable
            dataSource={medicalBenefitCoverageData}
            columns={getRiskRatioTableColumns(
              "Critical Illnesses / Medical Benefit Coverage"
            )}
          />
          <RiskRatioTable
            dataSource={tpdCoverageData}
            columns={getRiskRatioTableColumns(
              "Total and Permanent Disability Coverage"
            )}
          />
          <RiskRatioTable
            dataSource={comatoseCoverageData}
            columns={getRiskRatioTableColumns("Comatose Coverage")}
          />
        </div>
      </SectionContainer>
    </>
  );
};

RiskRatios.defaultProps = {
  personalInfo: {},
  currentReporting: {},
};

export default RiskRatios;
